import React, { useEffect, useMemo, useState } from 'react';
import {
    IntakeFormData,
    IntakeSchema,
    IntakeAppointmentInfo,
    YesNo,
    emptyIntakeForm,
    submitIntake,
} from '../../services/intakeService';
import { IntakeStrings, IntakeLanguage, getIntakeStrings, resolveFormLanguage } from '../../translations/intake';
import SignaturePad from './SignaturePad';
import Logo from '../Logo';
import BrandingLogo from '../branding/BrandingLogo';
import { API_BASE_URL } from '../../services/intakeService';

interface IntakeWizardProps {
    token: string;
    appointment: IntakeAppointmentInfo;
    schema: IntakeSchema;
    initialForm: IntakeFormData | null;
    onSubmitted: () => void;
}

const TOTAL_STEPS = 7;

// Curated list of countries SeaDent regularly sees patients from. Names are
// rendered via Intl.DisplayNames in the form's active language — no manual
// translation needed.
const COUNTRY_CODES = [
    'DE', 'ES', 'AT', 'CH', 'GB', 'IE', 'FR', 'IT', 'NL', 'BE', 'LU',
    'DK', 'SE', 'NO', 'FI', 'IS', 'PL', 'CZ', 'SK', 'HU', 'PT', 'GR',
    'RU', 'UA', 'BY', 'LT', 'LV', 'EE', 'RO', 'BG', 'HR', 'SI', 'RS',
    'TR', 'US', 'CA', 'AR', 'BR', 'MX', 'AU', 'NZ', 'ZA',
];

// Required-field discovery — returns the first missing field with its target
// step so the wizard can jump+scroll to it on a failed submit.
type FirstMissing = { fieldId: string; step: number };
function findFirstMissingField(form: IntakeFormData, schema: IntakeSchema): FirstMissing | null {
    // Step 1 — only name + DOB are hard requirements. Other personal fields
    // help the practice but aren't safety-critical.
    if (!form.personal.name || form.personal.name.trim().length < 2) {
        return { fieldId: 'intake-personal-name', step: 1 };
    }
    if (!form.personal.date_of_birth) {
        return { fieldId: 'intake-personal-dob', step: 1 };
    }
    // Step 2 — every cardiovascular + general flag must be answered (yes/no).
    for (const flag of schema.health_flags.cardiovascular) {
        if (!form.health.cardiovascular[flag]) {
            return { fieldId: `intake-cardio-${flag}`, step: 2 };
        }
    }
    for (const flag of schema.health_flags.general) {
        if (!form.health.general[flag]) {
            return { fieldId: `intake-general-${flag}`, step: 2 };
        }
    }
    // Step 3 — infectious + allergies.
    for (const flag of schema.health_flags.infectious) {
        if (!form.health.infectious[flag]) {
            return { fieldId: `intake-infectious-${flag}`, step: 3 };
        }
    }
    for (const flag of schema.health_flags.allergies) {
        if (!form.health.allergies[flag]) {
            return { fieldId: `intake-allergy-${flag}`, step: 3 };
        }
    }
    // Step 4 — pregnancy + xray.
    if (!form.pregnancy.is_pregnant) {
        return { fieldId: 'intake-pregnancy', step: 4 };
    }
    if (!form.xray.had_xray) {
        return { fieldId: 'intake-xray', step: 4 };
    }
    // Step 5 — special meds + major surgery.
    for (const key of schema.special_medication_keys) {
        if (!form.special_medications[key]?.value) {
            return { fieldId: `intake-special-${key}`, step: 5 };
        }
    }
    if (!form.major_surgery.value) {
        return { fieldId: 'intake-surgery', step: 5 };
    }
    // Step 6 — consent + signature + place.
    if (!form.consent_accepted) {
        return { fieldId: 'intake-consent', step: 6 };
    }
    if (!form.signed_place || form.signed_place.trim().length < 2) {
        return { fieldId: 'intake-signed-place', step: 6 };
    }
    if (!form.signature_data_url) {
        return { fieldId: 'intake-signature', step: 6 };
    }
    return null;
}

function getCountryOptions(language: string): { code: string; name: string }[] {
    let dn: Intl.DisplayNames | null = null;
    try {
        // Intl.DisplayNames is supported in all modern browsers (Chrome 81+,
        // Safari 14+, Firefox 86+). Wrap in try/catch for unsupported envs.
        dn = new Intl.DisplayNames([language], { type: 'region' });
    } catch {
        dn = null;
    }
    const items = COUNTRY_CODES.map((code) => ({
        code,
        name: (dn && dn.of(code)) || code,
    }));
    items.sort((a, b) => a.name.localeCompare(b.name, language));
    return items;
}

// ------------- Tiny UI primitives kept inline ------------------

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> =
    ({ title, subtitle, children }) => (
        <div>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#384860]">{title}</h2>
            {subtitle && <p className="mt-2 text-[#384860]/70">{subtitle}</p>}
            <div className="mt-6 space-y-5">{children}</div>
        </div>
    );

interface TextInputProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'url' | 'search';
    autoComplete?: string;
    required?: boolean;
    placeholder?: string;
    /** Stable id for scroll/focus targeting from validation. */
    id?: string;
}
const TextInput: React.FC<TextInputProps> = ({
    label, value, onChange, type = 'text', inputMode, autoComplete, required, placeholder, id: idProp,
}) => {
    const fallbackId = useMemo(() => `f-${Math.random().toString(36).slice(2, 9)}`, []);
    const id = idProp || fallbackId;
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-[#384860]">
                {label}{required && <span className="text-[#ff6f59] ml-1" aria-hidden>*</span>}
            </label>
            <input
                id={id}
                type={type}
                inputMode={inputMode}
                autoComplete={autoComplete}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1.5 block w-full min-h-[44px] rounded-xl border border-[#384860]/20 bg-white px-4 py-2.5 text-base text-[#384860] placeholder:text-[#384860]/40 focus:outline-none focus:ring-2 focus:ring-[#384860] focus:border-transparent transition-shadow"
            />
        </div>
    );
};

interface SelectInputProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    placeholder?: string;
    required?: boolean;
    id?: string;
}
const SelectInput: React.FC<SelectInputProps> = ({ label, value, onChange, options, placeholder, required, id: idProp }) => {
    const fallbackId = useMemo(() => `f-${Math.random().toString(36).slice(2, 9)}`, []);
    const id = idProp || fallbackId;
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-[#384860]">
                {label}{required && <span className="text-[#ff6f59] ml-1" aria-hidden>*</span>}
            </label>
            <select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`mt-1.5 block w-full min-h-[44px] rounded-xl border border-[#384860]/20 bg-white px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#384860] focus:border-transparent transition-shadow ${value ? 'text-[#384860]' : 'text-[#384860]/40'}`}
            >
                {placeholder !== undefined && <option value="">{placeholder}</option>}
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value} className="text-[#384860]">{opt.label}</option>
                ))}
            </select>
        </div>
    );
};

const YesNoToggle: React.FC<{
    label: string;
    value: YesNo;
    onChange: (v: YesNo) => void;
    yesLabel: string;
    noLabel: string;
    unsureLabel: string;
    id?: string;
}> = ({ label, value, onChange, yesLabel, noLabel, unsureLabel, id }) => {
    // Bigger button label set ("No estoy seguro" can be 14 chars) means we'd
    // squeeze the question text in narrow columns. Use a vertical layout on
    // very narrow viewports — fall back to inline on md+.
    const baseBtn = 'min-h-[40px] px-2 text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap';
    return (
        <div id={id} tabIndex={-1} className="py-2 px-2 rounded-xl hover:bg-[#f9f9f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#384860] transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            {/* min-w-0 lets the flex child shrink past its content width so long
                German compounds wrap instead of pushing the buttons out of the
                column. hyphens-auto needs the lang attribute on an ancestor. */}
            <span className="text-sm text-[#384860] min-w-0 flex-1 leading-snug break-words hyphens-auto">
                {label}
            </span>
            <div role="radiogroup" aria-label={label} className="flex shrink-0 self-stretch sm:self-auto rounded-lg border border-[#384860]/20 bg-white overflow-hidden">
                <button
                    type="button"
                    role="radio"
                    aria-checked={value === 'yes'}
                    onClick={() => onChange(value === 'yes' ? null : 'yes')}
                    className={`${baseBtn} flex-1 sm:flex-none sm:min-w-[44px] border-r border-[#384860]/20 ${
                        value === 'yes'
                            ? 'bg-[#384860] text-white'
                            : 'text-[#384860] hover:bg-[#384860]/5'
                    }`}
                >
                    {yesLabel}
                </button>
                <button
                    type="button"
                    role="radio"
                    aria-checked={value === 'unsure'}
                    onClick={() => onChange(value === 'unsure' ? null : 'unsure')}
                    className={`${baseBtn} flex-1 sm:flex-none px-3 border-r border-[#384860]/20 ${
                        value === 'unsure'
                            ? 'bg-amber-500 text-white'
                            : 'text-[#384860] hover:bg-[#384860]/5'
                    }`}
                >
                    {unsureLabel}
                </button>
                <button
                    type="button"
                    role="radio"
                    aria-checked={value === 'no'}
                    onClick={() => onChange(value === 'no' ? null : 'no')}
                    className={`${baseBtn} flex-1 sm:flex-none sm:min-w-[44px] ${
                        value === 'no'
                            ? 'bg-[#384860] text-white'
                            : 'text-[#384860] hover:bg-[#384860]/5'
                    }`}
                >
                    {noLabel}
                </button>
            </div>
        </div>
    );
};

const Toast: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => {
    useEffect(() => {
        const t = setTimeout(onDismiss, 6000);
        return () => clearTimeout(t);
    }, [message, onDismiss]);
    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed inset-x-4 top-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-md z-50 rounded-2xl bg-white shadow-xl border border-amber-200 p-4 flex items-start gap-3 animate-fadeIn"
        >
            <div className="shrink-0 h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 9v4M12 17h.01" />
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
            </div>
            <p className="text-sm md:text-[15px] text-[#384860] leading-snug flex-1">{message}</p>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Close"
                className="shrink-0 -m-1 p-1 text-[#384860]/60 hover:text-[#384860] cursor-pointer"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>
    );
};

const ProgressBar: React.FC<{ current: number; total: number; stepLabels: string[]; stepOfFmt: string }> =
    ({ current, total, stepLabels, stepOfFmt }) => {
        const pct = Math.round((current / total) * 100);
        const text = stepOfFmt.replace('{{current}}', String(current)).replace('{{total}}', String(total));
        return (
            <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[#384860] uppercase tracking-wide">{text}</span>
                    <span className="text-xs text-[#384860]/60">{stepLabels[current - 1]}</span>
                </div>
                <div className="h-2 w-full bg-[#384860]/10 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#384860] to-[#93cbb6] transition-all duration-300 rounded-full"
                        style={{ width: `${pct}%` }}
                        role="progressbar"
                        aria-valuenow={current}
                        aria-valuemin={1}
                        aria-valuemax={total}
                    />
                </div>
            </div>
        );
    };

// ------------- Wizard ------------------

const IntakeWizard: React.FC<IntakeWizardProps> = ({ token, appointment, schema, initialForm, onSubmitted }) => {
    // The patient can override the appointment language right inside the form.
    // We seed from appointment.language but then drive everything off this state.
    const [lang, setLang] = useState<IntakeLanguage>(() =>
        initialForm ? resolveFormLanguage(initialForm.language) : resolveFormLanguage(appointment.language),
    );

    // For languages we don't fully translate yet (RU), resolveFormLanguage maps
    // them to a real one so countries, hyphenation and html lang stay consistent.
    const effectiveLang = lang;
    const strings: IntakeStrings = useMemo(() => getIntakeStrings(lang), [lang]);
    const countryOptions = useMemo(
        () => getCountryOptions(effectiveLang).map((c) => ({ value: c.code, label: c.name })),
        [effectiveLang],
    );

    // Keep the document language in sync with the user's choice — drives
    // hyphenation, screen-reader pronunciation and Intl APIs further down.
    useEffect(() => {
        document.documentElement.lang = effectiveLang;
    }, [effectiveLang]);

    // Initial form state — merge prefill from appointment with any saved draft.
    const [form, setForm] = useState<IntakeFormData>(() => {
        if (initialForm) return initialForm;
        const base = emptyIntakeForm(appointment.language);
        base.personal.name = appointment.patient_name;
        base.personal.email = appointment.patient_email;
        base.personal.phone_private = appointment.patient_phone;
        // Sea-Dent is in Palma, so 99% of patients sign here — default the place
        // so they can hit submit without typing it. They can still overwrite.
        base.signed_place = 'Palma de Mallorca';
        return base;
    });

    // Persist the chosen language into the submitted payload so the server
    // (PDF, email, archive) uses the language the patient actually filled in.
    const handleLangChange = (next: IntakeLanguage) => {
        setLang(next);
        setForm((f) => ({ ...f, language: next }));
    };

    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    // ---------- helpers to update nested form state immutably ----------
    const updatePersonal = (k: keyof IntakeFormData['personal'], v: string) =>
        setForm((f) => ({ ...f, personal: { ...f.personal, [k]: v } }));

    const updateHealthFlag = (
        group: keyof IntakeFormData['health'],
        flag: string,
        v: YesNo,
    ) => setForm((f) => ({
        ...f,
        health: { ...f.health, [group]: { ...f.health[group], [flag]: v } },
    }));

    const updateSpecial = (key: string, patch: Partial<{ value: YesNo; since: string }>) =>
        setForm((f) => ({
            ...f,
            special_medications: {
                ...f.special_medications,
                [key]: {
                    value: patch.value !== undefined ? patch.value : (f.special_medications[key]?.value ?? null),
                    since: patch.since !== undefined ? patch.since : (f.special_medications[key]?.since ?? ''),
                },
            },
        }));

    const addMedication = () =>
        setForm((f) => ({ ...f, medications: [...f.medications, { name: '', since: '' }] }));

    const updateMedication = (idx: number, key: 'name' | 'since', v: string) =>
        setForm((f) => ({
            ...f,
            medications: f.medications.map((m, i) => (i === idx ? { ...m, [key]: v } : m)),
        }));

    const removeMedication = (idx: number) =>
        setForm((f) => ({ ...f, medications: f.medications.filter((_, i) => i !== idx) }));

    // Toggle eines Marketing-Kanals. granted_at wird beim Submit serverseitig
    // gesetzt — Client-Wert wird vom Backend stets überschrieben.
    const updateMarketingChannel = (
        channel: 'email' | 'whatsapp' | 'phone',
        granted: boolean,
    ) => setForm((f) => ({
        ...f,
        marketing_consent: {
            ...f.marketing_consent,
            [channel]: {
                granted,
                granted_at: granted ? new Date().toISOString() : null,
            },
        },
    }));

    // ---------- submit ----------
    const handleSubmit = async () => {
        // Walk the required-field list; if anything is missing, jump to the
        // owning step, scroll the field into view, and surface a friendly toast.
        const missing = findFirstMissingField(form, schema);
        if (missing) {
            setStep(missing.step);
            setToast(strings.validation.toast_incomplete);
            // Two RAFs: first lets React commit the new step, second runs after
            // the new step's DOM is painted so the target element exists.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const el = document.getElementById(missing.fieldId);
                    if (!el) return;
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Focus an interactive child if present; else the wrapper.
                    const focusable = el.matches('input, select, textarea, button')
                        ? (el as HTMLElement)
                        : el.querySelector<HTMLElement>('input, select, textarea, button[role="radio"], button');
                    focusable?.focus({ preventScroll: true });
                });
            });
            return;
        }

        setSubmitting(true);
        setSubmitError(null);
        try {
            await submitIntake(token, form);
            onSubmitted();
        } catch (err: any) {
            setSubmitError(err?.message || strings.meta.error_generic);
            setSubmitting(false);
        }
    };

    const stepLabels = [
        strings.steps.personal,
        strings.steps.health,
        strings.steps.infectious,
        strings.steps.medications,
        strings.steps.special_meds,
        strings.steps.consent,
        strings.steps.marketing,
    ];

    const canGoNext = step < TOTAL_STEPS;
    const isLast = step === TOTAL_STEPS;

    // ---------- step bodies ----------
    const renderStep = () => {
        if (step === 1) {
            return (
                <Section title={strings.personal.section_title}>
                    <TextInput id="intake-personal-name" label={strings.personal.name} value={form.personal.name}
                        onChange={(v) => updatePersonal('name', v)} required autoComplete="name" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <TextInput id="intake-personal-dob" label={strings.personal.date_of_birth} value={form.personal.date_of_birth}
                            onChange={(v) => updatePersonal('date_of_birth', v)} type="date" required autoComplete="bday" />
                        <TextInput label={strings.personal.place_of_birth} value={form.personal.place_of_birth}
                            onChange={(v) => updatePersonal('place_of_birth', v)} />
                    </div>
                    <TextInput label={strings.personal.street} value={form.personal.street}
                        onChange={(v) => updatePersonal('street', v)} autoComplete="street-address" />
                    <div className="grid grid-cols-3 gap-5">
                        <TextInput label={strings.personal.postal_code} value={form.personal.postal_code}
                            onChange={(v) => updatePersonal('postal_code', v)} inputMode="numeric" autoComplete="postal-code" />
                        <div className="col-span-2">
                            <TextInput label={strings.personal.city} value={form.personal.city}
                                onChange={(v) => updatePersonal('city', v)} autoComplete="address-level2" />
                        </div>
                    </div>
                    <SelectInput
                        label={strings.personal.country}
                        value={form.personal.country}
                        onChange={(v) => updatePersonal('country', v)}
                        options={countryOptions}
                        placeholder={strings.personal.country_placeholder}
                    />
                    <TextInput label={strings.personal.email} value={form.personal.email}
                        onChange={(v) => updatePersonal('email', v)} type="email" inputMode="email" autoComplete="email" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <TextInput label={strings.personal.phone_private} value={form.personal.phone_private}
                            onChange={(v) => updatePersonal('phone_private', v)} type="tel" inputMode="tel" autoComplete="tel" />
                        <TextInput label={strings.personal.phone_work} value={form.personal.phone_work}
                            onChange={(v) => updatePersonal('phone_work', v)} type="tel" inputMode="tel" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <TextInput label={strings.personal.occupation} value={form.personal.occupation}
                            onChange={(v) => updatePersonal('occupation', v)} />
                        <TextInput label={strings.personal.nie} value={form.personal.nie}
                            onChange={(v) => updatePersonal('nie', v)} />
                    </div>
                    <div className="pt-2 border-t border-[#384860]/10">
                        <TextInput label={strings.personal.doctor_name} value={form.personal.doctor_name}
                            onChange={(v) => updatePersonal('doctor_name', v)} />
                        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                            <TextInput label={strings.personal.doctor_address} value={form.personal.doctor_address}
                                onChange={(v) => updatePersonal('doctor_address', v)} />
                            <TextInput label={strings.personal.doctor_phone} value={form.personal.doctor_phone}
                                onChange={(v) => updatePersonal('doctor_phone', v)} type="tel" inputMode="tel" />
                        </div>
                    </div>
                </Section>
            );
        }
        if (step === 2) {
            return (
                <Section title={strings.health.section_title} subtitle={strings.health.section_subtitle}>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5">
                        <h3 className="font-semibold text-[#384860] mb-2">{strings.health.group_cardiovascular}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
                            {schema.health_flags.cardiovascular.map((flag) => (
                                <YesNoToggle
                                    key={flag}
                                    id={`intake-cardio-${flag}`}
                                    label={strings.health.flags[flag] || flag}
                                    value={form.health.cardiovascular[flag] ?? null}
                                    onChange={(v) => updateHealthFlag('cardiovascular', flag, v)}
                                    yesLabel={strings.common.yes}
                                    noLabel={strings.common.no}
                                    unsureLabel={strings.common.unsure}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5">
                        <h3 className="font-semibold text-[#384860] mb-2">{strings.health.group_general}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
                            {schema.health_flags.general.map((flag) => (
                                <YesNoToggle
                                    key={flag}
                                    id={`intake-general-${flag}`}
                                    label={strings.health.flags[flag] || flag}
                                    value={form.health.general[flag] ?? null}
                                    onChange={(v) => updateHealthFlag('general', flag, v)}
                                    yesLabel={strings.common.yes}
                                    noLabel={strings.common.no}
                                    unsureLabel={strings.common.unsure}
                                />
                            ))}
                        </div>
                        {form.health.general.other_diseases === 'yes' && (
                            <div className="mt-3">
                                <TextInput label={strings.health.other_diseases_text_label}
                                    value={form.other_diseases_text}
                                    onChange={(v) => setForm((f) => ({ ...f, other_diseases_text: v }))} />
                            </div>
                        )}
                    </div>
                </Section>
            );
        }
        if (step === 3) {
            return (
                <Section title={strings.infectious.section_title} subtitle={strings.infectious.section_subtitle}>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5">
                        <h3 className="font-semibold text-[#384860] mb-2">{strings.infectious.group_infectious}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
                            {schema.health_flags.infectious.map((flag) => (
                                <YesNoToggle
                                    key={flag}
                                    id={`intake-infectious-${flag}`}
                                    label={strings.infectious.flags[flag] || flag}
                                    value={form.health.infectious[flag] ?? null}
                                    onChange={(v) => updateHealthFlag('infectious', flag, v)}
                                    yesLabel={strings.common.yes}
                                    noLabel={strings.common.no}
                                    unsureLabel={strings.common.unsure}
                                />
                            ))}
                        </div>
                        {form.health.infectious.other_infectious === 'yes' && (
                            <div className="mt-3">
                                <TextInput label={strings.infectious.other_infectious_text_label}
                                    value={form.other_infectious_text}
                                    onChange={(v) => setForm((f) => ({ ...f, other_infectious_text: v }))} />
                            </div>
                        )}
                    </div>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5">
                        <h3 className="font-semibold text-[#384860] mb-2">{strings.infectious.group_allergies}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
                            {schema.health_flags.allergies.map((flag) => (
                                <YesNoToggle
                                    key={flag}
                                    id={`intake-allergy-${flag}`}
                                    label={strings.infectious.flags[flag] || flag}
                                    value={form.health.allergies[flag] ?? null}
                                    onChange={(v) => updateHealthFlag('allergies', flag, v)}
                                    yesLabel={strings.common.yes}
                                    noLabel={strings.common.no}
                                    unsureLabel={strings.common.unsure}
                                />
                            ))}
                        </div>
                        <div className="mt-3">
                            <TextInput label={strings.infectious.metals_allergy_label}
                                value={form.metals_allergy_text}
                                onChange={(v) => setForm((f) => ({ ...f, metals_allergy_text: v }))} />
                        </div>
                    </div>
                </Section>
            );
        }
        if (step === 4) {
            return (
                <Section title={strings.medications.section_title}>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5 space-y-3">
                        <YesNoToggle
                            id="intake-pregnancy"
                            label={strings.medications.pregnancy_q}
                            value={form.pregnancy.is_pregnant}
                            onChange={(v) => setForm((f) => ({ ...f, pregnancy: { ...f.pregnancy, is_pregnant: v } }))}
                            yesLabel={strings.common.yes}
                            noLabel={strings.common.no}
                            unsureLabel={strings.common.unsure}
                        />
                        {form.pregnancy.is_pregnant === 'yes' && (
                            <TextInput label={strings.medications.pregnancy_month}
                                value={form.pregnancy.month}
                                onChange={(v) => setForm((f) => ({ ...f, pregnancy: { ...f.pregnancy, month: v } }))}
                                inputMode="numeric" />
                        )}
                    </div>
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5 space-y-3">
                        <YesNoToggle
                            id="intake-xray"
                            label={strings.medications.xray_q}
                            value={form.xray.had_xray}
                            onChange={(v) => setForm((f) => ({ ...f, xray: { ...f.xray, had_xray: v } }))}
                            yesLabel={strings.common.yes}
                            noLabel={strings.common.no}
                            unsureLabel={strings.common.unsure}
                        />
                        {form.xray.had_xray === 'yes' && (
                            <TextInput label={strings.medications.xray_when}
                                value={form.xray.when}
                                onChange={(v) => setForm((f) => ({ ...f, xray: { ...f.xray, when: v } }))}
                                placeholder="z.B. 03/2024" />
                        )}
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#384860] mb-2">{strings.medications.meds_q}</h3>
                        {form.medications.length === 0 && (
                            <p className="text-sm text-[#384860]/60 mb-3">{strings.medications.meds_empty_hint}</p>
                        )}
                        <div className="space-y-3">
                            {form.medications.map((med, idx) => (
                                <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                                    <TextInput label={strings.medications.meds_name} value={med.name}
                                        onChange={(v) => updateMedication(idx, 'name', v)} />
                                    <TextInput label={strings.medications.meds_since} value={med.since}
                                        onChange={(v) => updateMedication(idx, 'since', v)} placeholder="z.B. 2020" />
                                    <button type="button" onClick={() => removeMedication(idx)}
                                        className="min-h-[44px] px-4 rounded-xl border border-[#384860]/20 text-sm text-[#384860] hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors cursor-pointer">
                                        {strings.medications.meds_remove}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addMedication}
                            className="mt-3 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl bg-white border border-[#384860]/30 text-[#384860] font-medium hover:bg-[#384860]/5 transition-colors cursor-pointer">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            {strings.medications.meds_add}
                        </button>
                    </div>
                </Section>
            );
        }
        if (step === 5) {
            return (
                <Section title={strings.special.section_title}>
                    {schema.special_medication_keys.map((key) => {
                        const cur = form.special_medications[key] || { value: null, since: '' };
                        return (
                            <div key={key} className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5 space-y-3">
                                <YesNoToggle
                                    id={`intake-special-${key}`}
                                    label={strings.special.flags[key] || key}
                                    value={cur.value}
                                    onChange={(v) => updateSpecial(key, { value: v })}
                                    yesLabel={strings.common.yes}
                                    noLabel={strings.common.no}
                                    unsureLabel={strings.common.unsure}
                                />
                                {cur.value === 'yes' && (
                                    <TextInput label={strings.special.since} value={cur.since}
                                        onChange={(v) => updateSpecial(key, { since: v })} placeholder="z.B. 06/2023" />
                                )}
                            </div>
                        );
                    })}
                    <div className="rounded-2xl border border-[#384860]/15 bg-[#f9f9f7]/40 p-4 md:p-5 space-y-3">
                        <YesNoToggle
                            id="intake-surgery"
                            label={strings.special.major_surgery_q}
                            value={form.major_surgery.value}
                            onChange={(v) => setForm((f) => ({ ...f, major_surgery: { ...f.major_surgery, value: v } }))}
                            yesLabel={strings.common.yes}
                            noLabel={strings.common.no}
                            unsureLabel={strings.common.unsure}
                        />
                        {form.major_surgery.value === 'yes' && (
                            <TextInput label={strings.special.major_surgery_date} value={form.major_surgery.date}
                                onChange={(v) => setForm((f) => ({ ...f, major_surgery: { ...f.major_surgery, date: v } }))}
                                type="date" />
                        )}
                    </div>
                </Section>
            );
        }
        if (step === 6) {
            // consent + signature (Behandlungsvertrag)
            return (
                <Section title={strings.consent.section_title}>
                    <div className="rounded-2xl border border-[#384860]/20 bg-white p-5 md:p-6">
                        <p className="text-sm md:text-[15px] leading-relaxed text-[#384860]/90 whitespace-pre-wrap">
                            {strings.consent.block_text}
                        </p>
                    </div>
                    <label className="flex items-start gap-3 p-4 rounded-2xl bg-[#ff6f59]/5 border border-[#ff6f59]/30 cursor-pointer">
                        <input
                            id="intake-consent"
                            type="checkbox"
                            checked={form.consent_accepted}
                            onChange={(e) => setForm((f) => ({ ...f, consent_accepted: e.target.checked }))}
                            className="mt-0.5 h-5 w-5 rounded text-[#ff6f59] focus:ring-2 focus:ring-[#ff6f59] cursor-pointer"
                        />
                        <span className="text-sm md:text-base text-[#384860] font-medium">
                            {strings.consent.accept_checkbox}
                        </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <TextInput id="intake-signed-place" label={strings.consent.place_label} value={form.signed_place}
                            onChange={(v) => setForm((f) => ({ ...f, signed_place: v }))}
                            placeholder={strings.consent.place_placeholder} required />
                        <TextInput label={strings.consent.date_label}
                            value={new Date().toISOString().slice(0, 10)} onChange={() => { /* read-only */ }} type="date" />
                    </div>
                    <div id="intake-signature" tabIndex={-1}>
                        <label className="block text-sm font-medium text-[#384860] mb-1.5">
                            {strings.consent.signature_label}<span className="text-[#ff6f59] ml-1" aria-hidden>*</span>
                        </label>
                        <SignaturePad
                            value={form.signature_data_url}
                            onChange={(v) => setForm((f) => ({ ...f, signature_data_url: v }))}
                            clearLabel={strings.consent.signature_clear}
                            hint={strings.consent.signature_hint}
                            ariaLabel={strings.consent.signature_label}
                        />
                    </div>
                </Section>
            );
        }
        // step 7 — Marketing-Einwilligung (freiwillig, getrennt vom Behandlungs-Consent;
        // LOPDGDD + RGPD Art. 6.1.a, AEPD-konform granular je Kanal)
        const renderMarketingChannel = (
            channel: 'email' | 'whatsapp' | 'phone',
            label: string,
            description: string,
        ) => {
            const granted = form.marketing_consent[channel].granted;
            return (
                <label className="flex items-start gap-3 p-4 rounded-2xl bg-[#93cbb6]/5 border border-[#93cbb6]/40 cursor-pointer hover:bg-[#93cbb6]/10 transition-colors">
                    <input
                        id={`intake-marketing-${channel}`}
                        type="checkbox"
                        checked={granted}
                        onChange={(e) => updateMarketingChannel(channel, e.target.checked)}
                        className="mt-0.5 h-5 w-5 rounded text-[#93cbb6] focus:ring-2 focus:ring-[#93cbb6] cursor-pointer"
                    />
                    <span className="flex-1">
                        <span className="block text-sm md:text-base text-[#384860] font-medium">{label}</span>
                        <span className="block text-xs md:text-sm text-[#384860]/70 mt-0.5">{description}</span>
                    </span>
                </label>
            );
        };
        return (
            <Section title={strings.marketing.section_title}>
                {/* Pflichtinformationen nach Art. 13 RGPD / LOPDGDD */}
                <div className="rounded-2xl border border-[#384860]/20 bg-white p-5 md:p-6 space-y-3">
                    <p className="text-sm md:text-[15px] leading-relaxed text-[#384860]/90">
                        {strings.marketing.intro_paragraph}
                    </p>
                    <div>
                        <h3 className="text-sm font-semibold text-[#384860]">{strings.marketing.purpose_heading}</h3>
                        <p className="text-sm text-[#384860]/80 mt-1">{strings.marketing.purpose_text}</p>
                    </div>
                    <p className="text-xs md:text-sm text-[#384860]/70">{strings.marketing.legal_basis}</p>
                    <p className="text-xs md:text-sm text-[#384860]/70">{strings.marketing.retention}</p>
                    <p className="text-xs md:text-sm text-[#384860]/70">{strings.marketing.recipients}</p>
                    <div>
                        <h3 className="text-sm font-semibold text-[#384860]">{strings.marketing.rights_heading}</h3>
                        <p className="text-xs md:text-sm text-[#384860]/80 mt-1">{strings.marketing.rights_text}</p>
                    </div>
                </div>
                {/* Drei granulare Opt-In-Checkboxen — default unangekreuzt (AEPD-Pflicht) */}
                <div className="space-y-3">
                    {renderMarketingChannel('email',    strings.marketing.email_label,    strings.marketing.email_description)}
                    {renderMarketingChannel('whatsapp', strings.marketing.whatsapp_label, strings.marketing.whatsapp_description)}
                    {renderMarketingChannel('phone',    strings.marketing.phone_label,    strings.marketing.phone_description)}
                </div>
                <p className="text-xs md:text-sm text-[#384860]/60 italic">
                    {strings.marketing.opt_out_note}
                </p>
            </Section>
        );
    };

    return (
        <div lang={effectiveLang} className="min-h-screen bg-gradient-to-b from-[#f9f9f7] to-white pb-32">
            {/* Top branding bar */}
            <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#384860]/10">
                <div className="max-w-2xl mx-auto px-4 md:px-6 py-4 md:py-5">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <BrandingLogo
                                src={`${API_BASE_URL}/settings/branding/logo`}
                                alt="SeaDent"
                                className="h-10 md:h-12 w-auto max-w-[160px] shrink-0 object-contain"
                                fallback={<Logo className="h-10 md:h-11 w-auto shrink-0" color="#93cbb6" />}
                            />
                            <div className="leading-tight border-l border-[#384860]/15 pl-3 min-w-0">
                                <div className="text-xs md:text-sm font-medium text-[#384860] truncate">{strings.intro.title}</div>
                                <div className="text-[10px] md:text-xs text-[#384860]/60">Mallorca</div>
                            </div>
                        </div>
                        <div
                            role="radiogroup"
                            aria-label="Language"
                            className="shrink-0 flex rounded-lg border border-[#384860]/20 bg-white overflow-hidden text-xs font-semibold"
                        >
                            {(['de', 'en', 'es'] as const).map((code, i) => {
                                const active = lang === code;
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => handleLangChange(code)}
                                        className={`min-h-[32px] px-2.5 transition-colors cursor-pointer uppercase tracking-wide ${
                                            i > 0 ? 'border-l border-[#384860]/20' : ''
                                        } ${
                                            active
                                                ? 'bg-[#384860] text-white'
                                                : 'text-[#384860] hover:bg-[#384860]/5'
                                        }`}
                                    >
                                        {code}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <ProgressBar
                        current={step}
                        total={TOTAL_STEPS}
                        stepLabels={stepLabels}
                        stepOfFmt={strings.nav.step_of}
                    />
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
                {/* Intro on first step */}
                {step === 1 && (
                    <div className="mb-8 space-y-3">
                        <div className="inline-flex items-center gap-2 text-sm font-medium italic text-[#384860] tracking-wide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                            {strings.intro.motto}
                        </div>
                        <p className="text-[#384860] text-xl md:text-2xl font-semibold leading-tight">
                            {strings.intro.greeting}
                        </p>
                        <p className="text-[#384860]/80 text-base md:text-lg leading-relaxed">{strings.intro.subtitle}</p>
                        <p className="text-sm text-[#384860]/60">{strings.intro.confidentiality}</p>
                    </div>
                )}

                {renderStep()}

                {submitError && (
                    <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
                        {submitError}
                    </div>
                )}
            </main>

            {/* Sticky bottom action bar */}
            <nav className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-[#384860]/10 shadow-[0_-4px_20px_-4px_rgba(8,145,178,0.08)]">
                <div className="max-w-2xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setStep((s) => Math.max(1, s - 1))}
                        disabled={step === 1 || submitting}
                        className="min-h-[48px] px-5 rounded-xl border border-[#384860]/20 text-[#384860] font-medium hover:bg-[#384860]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                        {strings.nav.back}
                    </button>
                    <div className="flex-1" />
                    {canGoNext && (
                        <button
                            type="button"
                            onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
                            className="min-h-[48px] px-6 rounded-xl bg-[#384860] text-white font-semibold hover:bg-[#263245] shadow-sm hover:shadow-md transition-all cursor-pointer"
                        >
                            {strings.nav.next}
                        </button>
                    )}
                    {isLast && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="min-h-[48px] px-6 rounded-xl bg-[#ff6f59] text-white font-semibold hover:bg-[#e55a45] shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {submitting ? strings.nav.submitting : strings.nav.submit}
                        </button>
                    )}
                </div>
            </nav>

            {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
        </div>
    );
};

export default IntakeWizard;
